import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { parseBannerList } from 'helpers/parseBannerList';

import { Slider } from '@atlas/components';
import {
  AccountManager,
  btag,
  pushToDataLayer,
  useApplicationState,
  useBannerGroupList,
  useCasinoNewState,
} from '@atlas/core';

import { filterBannersList } from 'common/utils/filterBannersList';
import { isUrlAbsolute } from 'common/utils/utils';
import history from 'components/MainRouter/history';
import { GROUPS } from 'components/Pages/Casino/constants';
import { useIsRtl } from 'hooks/useIsRtl';

export const CasinoBanner = () => {
  const { authenticated } = useApplicationState();
  const { group } = useCasinoNewState();
  const { bannersChunk, isLoading } = useBannerGroupList(
    'casino',
    'mobile',
    group === 'LIVE' && 'live_mobile',
  );
  const isRtl = useIsRtl();
  const preloadedImages = useRef(new Set());
  const [sliderInstance, setSliderInstance] = useState(null);

  const groups = useMemo(() => bannersChunk.map(parseBannerList), [bannersChunk]);
  const dir = useMemo(() => (isRtl ? 'rtl' : 'ltr'), [isRtl]);

  const filteredGroups = useMemo(
    () =>
      groups
        .map(group => filterBannersList(group, authenticated))
        .filter(group => group.length > 0),
    [authenticated, groups],
  );

  useEffect(() => {
    if (filteredGroups.length > 0) {
      const firstGroup = filteredGroups[0];
      const firstBanner = firstGroup[0];

      if (firstBanner && !preloadedImages.current.has(firstBanner.imageUrl)) {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = firstBanner.imageUrl;
        link.fetchPriority = 'high';
        document.head.appendChild(link);
        preloadedImages.current.add(firstBanner.imageUrl);
      }
    }
  }, [filteredGroups]);

  useEffect(() => {
    const handleUpdate = () => {
      if (sliderInstance) {
        sliderInstance.update();
        sliderInstance.autoplay?.start();
      }
    };

    window.addEventListener('resize', handleUpdate);
    return () => {
      window.removeEventListener('resize', handleUpdate);
    };
  }, [sliderInstance]);

  const onBeforeSwiperInit = swiperInstance => {
    setSliderInstance(swiperInstance);
  };

  const handleClick = useCallback(
    (event, { href, imageUrl, target }) => {
      const { clientId } = AccountManager.account;

      pushToDataLayer(
        group === GROUPS.CASINO ? 'сasino_banner_click' : 'live_сasino_banner_click',
        { client: { id: clientId } },
        {
          image_URL: imageUrl,
        },
        btag,
      );

      const isInternalLink =
        href && !isUrlAbsolute(href) && !href.startsWith('//') && target !== '_blank';

      if (isInternalLink) {
        event?.preventDefault();
        history.push(href.startsWith('/') ? href : `/${href}`);
      }
    },
    [group],
  );

  if (!filteredGroups.length && !isLoading) return null;

  if (isLoading) {
    return <div className="casino-banner-skeleton" />;
  }

  return (
    <Slider
      autoplay={{
        delay: 5000,
        disableOnInteraction: false,
        pauseOnMouseEnter: false,
      }}
      dir={dir}
      isNewDesign
      loop={filteredGroups.length > 1}
      onBeforeInit={onBeforeSwiperInit}
      slidesPerView={1}
    >
      {filteredGroups.map((group, groupIndex) => (
        <Slider.Slide
          className={cn('casino-banner-slide', { 'has-progress-bar': filteredGroups.length > 1 })}
          key={group.reduce((acc, next) => `${acc}${next.imageUrl}`, '')}
        >
          <Slider.Group small>
            {group.map(({ href, imageUrl, target, title }, bannerIndex) => {
              const isFirstBanner = groupIndex === 0 && bannerIndex === 0;
              const loadingStrategy = isFirstBanner ? 'eager' : 'lazy';
              const fetchPriority = isFirstBanner ? 'high' : 'auto';

              return (
                <Slider.Banner
                  fetchPriority={fetchPriority}
                  href={href}
                  imageHeight="150"
                  imageUrl={imageUrl}
                  key={imageUrl}
                  loading={loadingStrategy}
                  onClick={event => handleClick(event, { href, imageUrl, target })}
                  small
                  target={target}
                  title={title}
                />
              );
            })}
          </Slider.Group>
        </Slider.Slide>
      ))}
    </Slider>
  );
};
